import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { OverlayModule } from '@angular/cdk/overlay';

import {
  NK_DEFAULT_RANGE_DATE_TIME_PARSERS,
  NK_RANGE_DATE_TIME_PARSERS,
  NkMomentRangeDateTimeModule,
  NkRangeDateTimeEditModule
} from '@nitro/reactor/datetime/index';
import { NkPickerModule } from '@nitro/reactor/common';
import { CommonMaterialModule } from '@nrwl/common-material';

import { AgentDownloadDialogComponent } from './components/agent.download.dialog/agent.download.dialog.component';
import { AgentDownloadDialogService } from './components/agent.download.dialog/agent.download.dialog.service';
import { CheckboxButtonComponent } from './components/checkbox.button/checkbox.button.component';
import { CheckboxDropdownComponent } from './components/checkbox.dropdown/checkbox.dropdown.component';
import { DialogComponent } from './components/dialog/dialog.component';
import { DialogService } from './components/dialog/dialog.service';
import { DetailsPopupComponent } from './components/details-popup/details-popup.component';
import { DetailsPopupService } from './components/details-popup/details-popup.service';
import { ExportFileComponent } from './components/export.file/export.file.component';
import { FixedRangeInputDialog } from './components/fixed.range.input.dialog/fixed.range.input.dialog';
import { HelpDialogComponent } from './components/helpdialog/helpdialog.component';
import { HelpIconComponent } from './components/helpicon/helpicon.component';
import { LoaderSpinnerComponent } from './components/loader/loader.spinner.component';
import { LogoWidgetComponent } from './components/logo.widget/logo.widget.component';
import { LogoWidgetService } from './components/logo.widget/logo.widget.service';
import { NotificationService } from './components/notifications/notifications.service';
import { ToastComponent } from './components/notifications/toast.component';
import { PasswordValidationService } from './components/password.validation/password.validation.service';
import { SelectOneDropDown } from './components/select.one.dropdown/select.one.dropdown.component';
import { StrengthMeterComponent } from './components/strength.meter/strength.meter.component';
import { TimePickerComponent } from './components/time-picker/time-picker.component';
import { UploadFileComponent } from './components/upload.file/upload.file.component';
import { UploadFileService } from './components/upload.file/upload.file.service';
import { VerticalSingleSliderComponent } from './components/vertical.slider/veritcal.single.slider.component';
import { VerticalSliderComponent } from './components/vertical.slider/vertical.slider.component';
import { BytePipe } from './pipes/byte/byte.pipe';
import { DomSanitizerPipe } from './pipes/dom-sanitizer/dom-sanitizer.pipe';
import { AnomaliesDurationPipe } from './pipes/duration/anomalies.duration.pipe';
import { ApiInterceptor } from './providers/api/api.interceptor';
import { FriendlyNamesService } from './providers/friendly.names/friendly.names.service';
import { ConsoleLoggerService } from './providers/logger/console.logger.service';
import { ApplicationsService } from './providers/application/applications.service';
import { LoggerService } from './providers/logger/logger.service';
import { UserAuthInterceptor } from './providers/user/user.auth.interceptor';
import { FieldMatchValidator } from './validators/field.match.validator';
import { MatchPatternValidator } from './validators/match.pattern.validator';
import { NumMaxValidator } from './validators/num.max.validator';
import { NumMinValidator } from './validators/num.min.validator';
import { PhoneNumberValidator } from './validators/phone.number.validator';
import { UniqueStringValidator } from './validators/unique.string.validator';
import { UniqueUrlValidator } from './validators/unique.url.validator';
import { ExternalURLService } from './providers/external.url/external.url.service';
import { ThresholdChartComponent } from './components/charts/threshold-chart/threshold.chart.component';
import { CountCardComponent } from './components/count.card/count.card.component';
import { ConversationComponent } from './components/conversation/conversation.component';
import { NkCheckboxColorDirective } from './components/conversation/nk-checkbox-color/nk-checkbox-color.directive';
import { NoDataComponent } from './components/no-data/no-data.component';
import { BreadCrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { TableViewListComponent } from './components/table-view-list/table-view-list.component';
import { FormatValueWithUnitPipe } from './pipes/formatValueWithUnit/formatValueWithUnit.pipe';
import { FormatValuePipe } from './pipes/formatValue/formatValue.pipe';
import { FormatService } from './services/format.service';
import { ActiveTestQueuedStateService } from './services/test-assigned/activeTestQueuedStateService';
import { SharedService } from './services/shared/shared.service';
import { NotificationComponent } from './components/notification/notification.component';
import { MonitorLicenseStatusComponent } from './components/monitor-license-status/monitor-license-status.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { AnomalyLaunchDrilldownService } from './services/anomaly-drilldown/anomaly-launch-drilldown.service';
import { SelectedTimeRangeComponent } from './components/selected.time.range/selected.time.range.component';
import { TopAnomalyTitleService } from './services/top-anomaly-title/top-anomaly-title.service';
import { AtwebLayoutProviderService } from './providers/layout.providers/atweb-layout-provider.service';
import { LayoutProviderService } from './providers/layout.providers/layout.provider.service';
import { WorkspaceLayoutComponent } from './components/workspace.layout/workspace.layout.component';
import { ProgressSpinnerComponent } from './components/progress-spinner/progress.spinner.component';
import { AnalyticsCommonService } from './providers/flows.analytics-dashboard/analytics.common.service';
import { AboutComponent } from './components/about/about.component';
import { SideBarComponent } from './components/side-bar/side-bar.component';
import { CommonDropdownComponent } from './common-dropdown/common-dropdown.component';
import { DateTimeSelectorComponent } from './components/date-time-selector/date-time-selector.component';
import { HelpMenuDialogComponent } from './components/help-menu-dialog/help-menu-dialog.component';
import { HelpMenuDialogService } from './components/help-menu-dialog/help-menu-dialog.service';
import { AutoRefreshService } from './services/auto-refresh/auto-refresh.service';
import { UtilsService } from './services/utils/utils.service';
import { SchedulerComponent } from './components/scheduler/scheduler.component';
import { HighlightSearch } from './pipes/highlightSearch/highlightSearch.pipe';
import { SSOLoginNavigatorComponent } from './components/sso.login.navigator/sso.login.navigator.component';
import { TestAssembleService } from './services/lmap-service/test-assemble';
import { HelpersService } from './services/lmap-service/helpers';
import { LmapService } from './services/lmap-service/lmap';
import { TestServerConfigService } from './services/lmap-service/testServerConfig';
import { BulletChartComponent } from './components/charts/bullet-chart/bullet-chart.component';
import { CommonDialogComponent } from './components/common-dialog/common-dialog.component';
import { BulletinConfigurationComponent } from './components/bulletin/bulletin-configuration/bulletin.configuration.component';
import { BulletinPopupComponent } from './components/bulletin/bulletin-popup/bulletin.popup.component';
import { TestAssignedProgressBarComponent } from './components/test-assigned-progress-bar/test-assigned-progress-bar.component';
import { TabulatorService } from './providers/tabulator/tabulator.service';
import { DialGaugeComponent } from './components/charts/dial-gauge/dial-gauge.component';
import { CommonFlowsSelectionComponent } from './components/common-flows-selection/common-flows-selection.component';
import { EndpointSelectionPanelComponent } from './endpoint-selection-panel/endpoint-selection-panel.component';
import { CommonFlowListComponent } from './components/common-flows-selection/common-flow-list/common-flow-list.component';
import { SparkLineChartComponent } from './components/charts/spark-line-chart/spark-line-chart.component';
import { CodeEditorComponent } from './components/code-editor/code-editor.component';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { publicWebAccessComponent } from './components/public-web-access/public-web-access.component';
import { DmzSystemSettingService } from './services/dmz-system-setting/dmz-system-setting.service';
import { StreamMetricsChartComponent } from './components/stream-metrics-chart/stream-metrics-chart.component';
import { AppsMultiStepSetupService } from './services/multistep/multi-step-setup.service';
import { PieChartComponent } from './components/charts/pie-chart/pie-chart.component';
import { LogsComponent } from './components/logs/logs.component';
import { SolidGaugeComponent } from './components/charts/solid-gauge/solid-gauge.component';
@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    CommonMaterialModule,
    FormsModule,
    ReactiveFormsModule,
    NkPickerModule,
    NkRangeDateTimeEditModule,
    NkMomentRangeDateTimeModule,
    RouterModule.forChild([]),
    OverlayModule,
    CodemirrorModule
  ],
  declarations: [
    DialogComponent,
    DetailsPopupComponent,
    LoaderSpinnerComponent,
    ProgressSpinnerComponent,
    StrengthMeterComponent,
    AgentDownloadDialogComponent,
    FieldMatchValidator,
    PhoneNumberValidator,
    UploadFileComponent,
    LogoWidgetComponent,
    MatchPatternValidator,
    NumMinValidator,
    NumMaxValidator,
    UniqueStringValidator,
    UniqueUrlValidator,
    FixedRangeInputDialog,
    DomSanitizerPipe,
    TimePickerComponent,
    ExportFileComponent,
    VerticalSliderComponent,
    VerticalSingleSliderComponent,
    BytePipe,
    HighlightSearch,
    ToastComponent,
    SelectOneDropDown,
    CheckboxDropdownComponent,
    CheckboxButtonComponent,
    AnomaliesDurationPipe,
    HelpIconComponent,
    HelpDialogComponent,
    ThresholdChartComponent,
    BulletChartComponent,
    CountCardComponent,
    ConversationComponent,
    NkCheckboxColorDirective,
    NoDataComponent,
    BreadCrumbComponent,
    TableViewListComponent,
    FormatValueWithUnitPipe,
    FormatValuePipe,
    NotificationComponent,
    MonitorLicenseStatusComponent,
    PaginatorComponent,
    SchedulerComponent,
    SelectedTimeRangeComponent,
    WorkspaceLayoutComponent,
    AboutComponent,
    SideBarComponent,
    CommonDropdownComponent,
    DateTimeSelectorComponent,
    HelpMenuDialogComponent,
    SSOLoginNavigatorComponent,
    CommonDialogComponent,
    BulletinConfigurationComponent,
    BulletinPopupComponent,
    TestAssignedProgressBarComponent,
    DialGaugeComponent,
    CommonFlowsSelectionComponent,
    EndpointSelectionPanelComponent,
    CommonFlowListComponent,
    SparkLineChartComponent,
    CodeEditorComponent,
    publicWebAccessComponent,
    StreamMetricsChartComponent,
    PieChartComponent,
    LogsComponent,
    SolidGaugeComponent
  ],
  // TODO(Cristian): if those are indeed common providers, there must be
  // preferred
  // 'providedIn: root' syntax inside provider itself for better DI management
  // (tree shaking) and removing them from here. I've removed already those that
  // already had that option inside @Injectable metadata;
  providers: [
    DialogService,
    DetailsPopupService,
    PasswordValidationService,
    AgentDownloadDialogService,
    UploadFileService,
    LogoWidgetService,
    FriendlyNamesService,
    NotificationService,
    AutoRefreshService,
    UtilsService,
    HelpersService,
    LmapService,
    TestAssembleService,
    TestServerConfigService,
    TabulatorService,
    DmzSystemSettingService,
    ActiveTestQueuedStateService,
    {
      provide: NK_RANGE_DATE_TIME_PARSERS,
      useValue: NK_DEFAULT_RANGE_DATE_TIME_PARSERS
    },
    {
      provide: LoggerService,
      useClass: ConsoleLoggerService
    },
    { provide: LayoutProviderService, useClass: AtwebLayoutProviderService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UserAuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true
    },
    ExternalURLService,
    FormatValueWithUnitPipe,
    FormatValuePipe,
    FormatService,
    SharedService,
    AnomalyLaunchDrilldownService,
    TopAnomalyTitleService,
    AtwebLayoutProviderService,
    AnalyticsCommonService,
    HelpMenuDialogService,
    ApplicationsService,
    AppsMultiStepSetupService
  ],
  exports: [
    LoaderSpinnerComponent,
    ProgressSpinnerComponent,
    StrengthMeterComponent,
    FieldMatchValidator,
    PhoneNumberValidator,
    UploadFileComponent,
    LogoWidgetComponent,
    AgentDownloadDialogComponent,
    MatchPatternValidator,
    NumMinValidator,
    NumMaxValidator,
    UniqueStringValidator,
    UniqueUrlValidator,
    DomSanitizerPipe,
    AnomaliesDurationPipe,
    TimePickerComponent,
    ExportFileComponent,
    VerticalSliderComponent,
    VerticalSingleSliderComponent,
    BytePipe,
    HighlightSearch,
    SelectOneDropDown,
    CheckboxDropdownComponent,
    CheckboxButtonComponent,
    HelpIconComponent,
    HelpDialogComponent,
    ThresholdChartComponent,
    BulletChartComponent,
    CountCardComponent,
    ConversationComponent,
    NkCheckboxColorDirective,
    NoDataComponent,
    BreadCrumbComponent,
    TableViewListComponent,
    FormatValueWithUnitPipe,
    FormatValuePipe,
    NotificationComponent,
    MonitorLicenseStatusComponent,
    PaginatorComponent,
    SchedulerComponent,
    DateTimeSelectorComponent,
    SelectedTimeRangeComponent,
    WorkspaceLayoutComponent,
    AboutComponent,
    SideBarComponent,
    CommonDropdownComponent,
    HelpMenuDialogComponent,
    CommonDialogComponent,
    TestAssignedProgressBarComponent,
    EndpointSelectionPanelComponent,
    DialGaugeComponent,
    CommonFlowListComponent,
    SparkLineChartComponent,
    CodeEditorComponent,
    publicWebAccessComponent,
    StreamMetricsChartComponent,
    PieChartComponent,
    LogsComponent,
    SolidGaugeComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
// TODO (Cristian):
// All providers from here that must be singletons (I think all of them, from
// providers folder), must be moved away from this library, into a new one
// 'shared-providers-lib' imported once into AppModule/PtcModule (depending on
// adding or not siblings for atweb.module)
export class CommonLibModule {
  /*constructor(@Optional() @SkipSelf() parentModule: CommonLibModule) {
    if (parentModule) {
      throw new Error(
        'CommonLibModule is already loaded. Import it in the AppModule only');
    }
  }*/
}
